/**
 * Utility functions for normalizing text for screen readers and accessibility
 */

/**
 * Normalizes measurement abbreviations and symbols in text for screen reader announcement.
 * Converts common abbreviations like "oz", "lb", '"' (inches) to full words.
 * Currently, all text and pluralization is limited to English.
 * This will need to be expanded upon should we want to be I18N compliant.
 *
 * @param text - The text to normalize (e.g., menu item name)
 * @returns The normalized text with measurements spelled out
 *
 * @example
 * normalizeTextForScreenReader('12oz steak') // '12 ounces steak'
 * normalizeTextForScreenReader('8" pizza') // '8 inch pizza'
 * normalizeTextForScreenReader('16oz drinks') // '16 ounces drinks'
 * normalizeTextForScreenReader('2lb burger') // '2 pound burger'
 */
export const normalizeTextForScreenReader = (text: string): string => {
  if (!text) return text

  let normalized = text

  // Weight measurements
  // Handle "oz" (ounce/ounces)
  // Pattern explanation: \d+ matches digits, (?:\/\d+|\.\d+)? matches either "/digits" OR ".digits" (not both)
  normalized = normalized.replace(
    /(\d+(?:\/\d+|\.\d+)?)\s*oz\b/gi,
    (_match, number) => {
      // Fractions are treated as singular (e.g., "1/2 ounce")
      if (number.includes('/')) {
        return `${number} ounce`
      }
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'ounce' : 'ounces'}`
    }
  )

  // Handle "lb" or "lbs" (pound/pounds)
  // Pattern explanation: \d+ matches digits, (?:\/\d+|\.\d+)? matches either "/digits" OR ".digits" (not both)
  normalized = normalized.replace(
    /(\d+(?:\/\d+|\.\d+)?)\s*lbs?\b/gi,
    (_match, number) => {
      // Fractions are treated as singular (e.g., "1/2 pound")
      if (number.includes('/')) {
        return `${number} pound`
      }
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'pound' : 'pounds'}`
    }
  )

  // IMPORTANT: Process longer/more-specific units BEFORE shorter ones (kg before g)
  // to prevent partial matches if whitespace tolerance is ever added to these patterns.
  // This ordering is load-bearing for future maintenance.

  // Handle "kg" (kilogram/kilograms) - must come before "g"
  // Pattern: \d+ matches digits, (?:\.\d+)? optionally matches decimal point followed by digits
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*kg\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'kilogram' : 'kilograms'}`
    }
  )

  // Handle "g" (gram/grams) - be careful not to match words ending in 'g'
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*g\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'gram' : 'grams'}`
    }
  )

  // Volume measurements
  // Handle "ml" (milliliter/milliliters)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*ml\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'milliliter' : 'milliliters'}`
    }
  )

  // Handle "l" (liter/liters) - be careful not to match words ending in 'l'
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*l\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'liter' : 'liters'}`
    }
  )

  // Handle "gal" (gallon/gallons)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*gal\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'gallon' : 'gallons'}`
    }
  )

  // Handle "qt" (quart/quarts)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*qt\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'quart' : 'quarts'}`
    }
  )

  // Handle "pt" (pint/pints)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*pt\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'pint' : 'pints'}`
    }
  )

  // Handle "fl oz" or "floz" (fluid ounce/fluid ounces)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*fl\s*oz\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} fluid ${num === 1 ? 'ounce' : 'ounces'}`
    }
  )

  // Linear measurements
  // Handle " (inches) - must come before ' (feet) to avoid conflicts
  normalized = normalized.replace(/(\d+(?:\.\d+)?)\s*"/g, (_match, number) => {
    const num = parseFloat(number)
    return `${number} ${num === 1 ? 'inch' : 'inches'}`
  })

  // Handle ' (feet)
  normalized = normalized.replace(/(\d+(?:\.\d+)?)\s*'/g, (_match, number) => {
    const num = parseFloat(number)
    return `${number} ${num === 1 ? 'foot' : 'feet'}`
  })

  // Handle "in" (inch/inches) - be careful not to match words containing 'in'
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*in\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'inch' : 'inches'}`
    }
  )

  // Handle "ft" (foot/feet)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*ft\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'foot' : 'feet'}`
    }
  )

  // Handle "cm" (centimeter/centimeters)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*cm\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'centimeter' : 'centimeters'}`
    }
  )

  // Handle "m" (meter/meters) - be very careful not to match words ending in 'm'
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*m\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'meter' : 'meters'}`
    }
  )

  // Temperature
  // Handle "°F" or "°f" (degrees Fahrenheit)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*°\s*F\b/gi,
    (_match, number) => `${number} degrees Fahrenheit`
  )

  // Handle "°C" or "°c" (degrees Celsius)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*°\s*C\b/gi,
    (_match, number) => `${number} degrees Celsius`
  )

  // Other common symbols and abbreviations
  // Handle "&" (and)
  normalized = normalized.replace(/\s*&\s*/g, ' and ')

  // Handle "#" (number)
  normalized = normalized.replace(/#(\d+)/g, 'number $1')

  // Handle "pcs" or "pc" (piece/pieces)
  normalized = normalized.replace(
    /(\d+(?:\.\d+)?)\s*pcs?\b/gi,
    (_match, number) => {
      const num = parseFloat(number)
      return `${number} ${num === 1 ? 'piece' : 'pieces'}`
    }
  )

  // Handle "ea" (each)
  normalized = normalized.replace(/(\d+(?:\.\d+)?)\s*ea\b/gi, '$1 each')

  // Handle "doz" or "dozen"
  normalized = normalized.replace(/(\d+(?:\.\d+)?)\s*doz\b/gi, '$1 dozen')

  // Clean up any extra spaces that might have been introduced
  normalized = normalized.replace(/\s+/g, ' ').trim()

  return normalized
}
